import React, { Fragment, useState } from 'react'
import { ModalTitle } from 'app/common/modals/title'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { media } from 'app/assets/styles'
import type { CacOrder } from 'shared/types'
import { ButtonFooter } from 'app/ecommerce/common/components'
import { H3, H4, H5, H6, SmallText, Text } from 'app/common/typography'
import { catalogType, formatPrice } from 'shared/utils'
import { colors } from 'shared/assets/styles'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import { fillCartWithMultipleProducts } from 'shared/store/ecommerce/order'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { getReusableOrder } from 'shared/store/ecommerce/catalog'
import type { RootState } from 'shared/store/reducers'
import { FastFoodBag } from 'app/assets/icons'
import { AlertModal } from 'app/common/modals/alert'
import { type State as EcommerceCatalogState } from 'shared/store/ecommerce/catalog'

const { lightGrey, red2, grey } = colors

type Props = {
    handleClose: () => void,
    order: CacOrder,
    intl: IntlShape
}

type StoreProps = {
    ecommerceCatalogState: EcommerceCatalogState
}

const _ReuseOrderModal = ({ order, handleClose, intl }: Props) => {
    const dispatch = useDispatch()
    const [error, setError] = useState(null)
    const storeProps: StoreProps = useSelector((state: RootState) => ({
        ecommerceCatalogState: state.ecommerceCatalogState
    }))
    const { ecommerceCatalogState } = storeProps
    const purchasableOrder = getReusableOrder(order, ecommerceCatalogState)

    const displayCustomization = (
        label?: string,
        initialQuantity?: number,
        quantity?: number,
        available?: boolean
    ) => {
        const text =
            (quantity || 0) > (initialQuantity || 0)
                ? 'clickandcollect.cart.custo.extra'
                : 'clickandcollect.cart.custo.without'
        const currentQty =
            initialQuantity && quantity && quantity - initialQuantity > 0
                ? quantity - initialQuantity
                : quantity
        return (
            <FormattedMessage
                id={text}
                values={{ product: label, qty: currentQty || 1 }}
                key={label}>
                {(t: string) => (
                    <SubText notAvailable={!available}>{t}</SubText>
                )}
            </FormattedMessage>
        )
    }

    const renderCustomization = (item: PurchasableItem, index?: number) => {
        const { customizedIngredients, options } = item
        return (
            <Fragment key={index}>
                {customizedIngredients &&
                    customizedIngredients.length > 0 &&
                    customizedIngredients.map(
                        ({ label, initialQuantity, quantity, available }) =>
                            displayCustomization(
                                label,
                                initialQuantity,
                                quantity,
                                available
                            )
                    )}
                {options &&
                    options.length > 0 &&
                    options.map((option, index) => (
                        <SmallText key={index} color={grey}>
                            {option.label}
                        </SmallText>
                    ))}
            </Fragment>
        )
    }

    const renderItemOrder = (orderContent: PurchasableItem, index: number) => {
        const { label, quantity, available } = orderContent
        return (
            <ContainerItem key={index}>
                <Quantity>
                    <Text>x{quantity}</Text>
                </Quantity>
                <div>
                    <MainText notAvailable={!available}>{label}</MainText>
                    {renderCustomization(orderContent)}
                    {orderContent.type === catalogType.MENU &&
                        !!orderContent.steps &&
                        orderContent.steps.length > 0 &&
                        orderContent.steps.map((step, index) =>
                            renderCustomization(step, index)
                        )}
                </div>
            </ContainerItem>
        )
    }

    const setCartAndNavigate = async () => {
        if (
            purchasableOrder.contentAvailable &&
            purchasableOrder.contentAvailable.length === 0
        ) {
            setError('clickandcollect.last.order.missing.all.products')
            return
        }
        dispatch(
            fillCartWithMultipleProducts(purchasableOrder.contentAvailable)
        )
        const result = _.filter(
            purchasableOrder.content,
            content =>
                !_.some(purchasableOrder.contentAvailable, contentAvailable =>
                    _.isEqual(content, contentAvailable)
                )
        )
        if (result.length > 0) {
            setError('clickandcollect.last.order.missing.products')
            return
        }
        dispatch(
            push(
                intl.formatMessage({
                    id: 'routes.clickandcollect.catalog'
                })
            )
        )
    }

    return (
        <Container>
            <TitleContainer>
                <ModalTitle
                    center
                    handleClose={handleClose}
                    titleNode={
                        <LastOrderDropDown>
                            <StyledFastFoodBag
                                arrowColor={colors.lightMayo}
                                size={32}
                            />
                            <FormattedMessage id="clickandcollect.catalog.trigger.order.again">
                                {(label: string) => <H5 center>{label}</H5>}
                            </FormattedMessage>
                        </LastOrderDropDown>
                    }
                />
            </TitleContainer>
            <div>
                {purchasableOrder.content &&
                    purchasableOrder.content.length > 0 &&
                    purchasableOrder.content.map((orderContent, index) =>
                        renderItemOrder(orderContent, index)
                    )}
            </div>
            <Line>
                <FormattedMessage id="clickandcollect.cart.total">
                    {(text: string) => <H4>{text}</H4>}
                </FormattedMessage>
                <H3>{formatPrice(purchasableOrder.price)}</H3>
            </Line>
            <ButtonFooter
                shouldStick
                labelKey="continue"
                onClick={setCartAndNavigate}
            />
            {error && (
                <AlertModal
                    isOpen={!!error}
                    alertTitleKey="clickandcollect.last.order.missing.products.title"
                    alertContentKey={error}
                    okLabel="ok"
                    handleClose={() => setError(null)}
                    onOkClick={() => {
                        setError(null)
                        purchasableOrder.contentAvailable &&
                        purchasableOrder.contentAvailable.length === 0
                            ? handleClose()
                            : dispatch(
                                  push(
                                      intl.formatMessage({
                                          id: 'routes.clickandcollect.catalog'
                                      })
                                  )
                              )
                    }}
                />
            )}
        </Container>
    )
}

const Container = styled.div`
    padding: 20px 0;
    ${media.medium`
        padding: 40px 0;
        margin: auto;
        min-height: 80vh;
    `};
`

const TitleContainer = styled.div`
    padding: 0 20px;
    ${media.medium`
        padding: 0 40px;
    `};
`

const LastOrderDropDown = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 50px;
`

const StyledFastFoodBag = styled(FastFoodBag)`
    margin-right: 10px;
`

const Line = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 20px;
    border: 0.5px solid ${lightGrey};
    border-right-width: 0;
    border-left-width: 0;
    position: fixed;
    bottom: 66px;
    ${media.medium`
        bottom: 75px;
    `};
    right: 0;
    left: 0;
`

const ContainerItem = styled.div`
    display: flex;
    flex: 1;
    margin: 15px 30px;
`

const Quantity = styled.div`
    margin-right: 20px;
`

const MainText = styled(H6)`
    ${props =>
        props.notAvailable &&
        `text-decoration-line: line-through;
        color: ${red2}`};
`

const SubText = styled(SmallText)`
    ${props =>
        props.notAvailable &&
        `text-decoration-line: line-through;
    color: ${red2}`};
`

export const ReuseOrderModal = injectIntl(_ReuseOrderModal)
